import Button from "@/components/base/Button";

interface CareIntroProps {
  eyebrow: string;
  title: string;
  paragraphs: string[];
  points?: string[];
  image: string;
  imageAlt: string;
  ctaLabel: string;
  ctaTo: string;
  ctaIcon?: string;
  reverse?: boolean;
}

/**
 * Editorial two-column introduction used on the specialist care pages.
 * The image and content columns swap sides when `reverse` is set.
 */
export default function CareIntro({
  eyebrow,
  title,
  paragraphs,
  points = [],
  image,
  imageAlt,
  ctaLabel,
  ctaTo,
  ctaIcon = "ri-arrow-right-line",
  reverse = false,
}: CareIntroProps) {
  return (
    <section className="bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className={`reveal relative lg:col-span-6 ${reverse ? "lg:order-2" : "lg:order-1"}`}>
            <div
              className="absolute -left-5 -top-5 bottom-16 w-2/3 rounded-2xl bg-primary-100/70"
              aria-hidden="true"
            ></div>
            <div className="relative overflow-hidden rounded-2xl border border-background-200 bg-background-100">
              <div className="h-[340px] w-full md:h-[480px]">
                <img
                  src={image}
                  alt={imageAlt}
                  title={imageAlt}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          <div className={`reveal lg:col-span-6 ${reverse ? "lg:order-1" : "lg:order-2"}`}>
            <span className="inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.18em] text-accent-700">
              <span className="h-px w-8 bg-accent-400" aria-hidden="true"></span>
              {eyebrow}
            </span>
            <h2 className="mt-5 font-heading text-3xl font-semibold leading-[1.14] text-foreground-950 md:text-4xl">
              {title}
            </h2>
            <div className="mt-5 space-y-4 font-body text-sm leading-relaxed text-foreground-600 md:text-base">
              {paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </div>

            {points.length ? (
              <ul className="mt-7 space-y-3">
                {points.map((point) => (
                  <li key={point} className="flex items-start gap-3 font-body text-sm text-foreground-700">
                    <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-accent-100 text-accent-700">
                      <i className="ri-check-line text-xs" aria-hidden="true"></i>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="mt-9">
              <Button to={ctaTo} size="lg" icon={ctaIcon}>
                {ctaLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}